// src/components/CartModal.js
import React, { useState, useEffect } from 'react';
import { FaTrash, FaShoppingCart, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

// Custom hook to detect mobile screens; default breakpoint is 600px
function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}


function CartModal({ show, onClose }) {
  const { cartItems, removeFromCart, clearCart, totalPrice } = useCart();
  const isMobile = useIsMobile();

  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formattedTotalPrice = totalPrice.toFixed(2);

  // --- Dynamic Style Adjustments ---

  const dynamicModalContentStyle = {
    ...modalContentStyle,
    padding: isMobile ? '1rem 1.5rem' : '1.5rem 2rem',
    width: isMobile ? '90%' : '480px',
    maxHeight: '85vh',
  };

  const dynamicItemRowStyle = {
    ...itemRowStyle,
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    gap: isMobile ? '0.5rem' : '1rem',
  };

  const dynamicItemImageStyle = {
    ...itemImageStyle,
    width: isMobile ? '40px' : '50px',
    height: isMobile ? '40px' : '50px',
  };

  const dynamicItemDetailsStyle = {
      ...itemDetailsStyle,
      flexBasis: isMobile ? '100%' : 'auto',
      marginTop: isMobile ? '0.5rem' : '0',
  };

   // Updated dynamicItemActionsStyle
   const dynamicItemActionsStyle = {
        ...itemActionsStyle, // Base now includes space-between and minWidth
        flexBasis: isMobile ? '100%' : 'auto',
        // Ensure vertical alignment is center on mobile wrap
        alignItems: isMobile ? 'center' : 'center',
        marginTop: isMobile ? '0.5rem' : '0',
        // Remove minWidth on mobile when it takes full basis
        minWidth: isMobile ? 'auto' : itemActionsStyle.minWidth, // Use base minWidth for desktop
   };


  const dynamicButtonsContainerStyle = {
    ...buttonsContainerStyle,
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    justifyContent: isMobile ? 'center' : 'space-between',
  };

  const dynamicClearCartButtonStyle = {
    ...clearCartButtonStyle,
    display: isMobile ? 'block' : 'inline-flex',
    width: isMobile ? '100%' : 'auto',
    textAlign: isMobile ? 'center' : 'left',
    marginBottom: isMobile ? '0.75rem' : '0',
    alignItems: isMobile ? undefined : 'center',
    justifyContent: isMobile ? undefined : 'center',
  };

  const dynamicBookRepairButtonStyle = {
     ...bookRepairButtonStyle,
     display: isMobile ? 'block' : 'inline-flex',
     width: isMobile ? '100%' : 'auto',
     textAlign: isMobile ? 'center' : 'left',
     alignItems: isMobile ? undefined : 'center',
     justifyContent: isMobile ? 'center' : 'center',
  };

  // --- JSX Structure ---

  return (
    <div
      className="cart-modal-backdrop"
      style={backdropStyle}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-modal-title"
    >
      <div className="cart-modal-content" style={dynamicModalContentStyle}>
        {/* Header */}
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaShoppingCart size={24} style={{ color: '#6c757d' }} />
            <h2 id="cart-modal-title" style={{ margin: 0, fontSize: '1.5rem', color: '#6c757d' }}>
              My Cart
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close cart"
            style={closeIconButtonStyle}
            onClick={onClose}
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Cart Content */}
        {cartItems.length === 0 ? (
          <p style={emptyCartStyle}>Your cart is empty.</p>
        ) : (
          <>
            <ul style={itemListStyle}>
              {cartItems.map((item) => (
                <li key={item.id} style={dynamicItemRowStyle}>
                  {item.image && (
                    <img src={item.image} alt="" style={dynamicItemImageStyle} />
                  )}
                  <div style={dynamicItemDetailsStyle}>
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#6c757d' }}>
                      {item.name}
                    </strong>
                  </div>
                  {/* Item Actions Container - Apply dynamic style here */}
                  <div style={dynamicItemActionsStyle}>
                    {/* Price Span - Update inline style */}
                    <span
                      style={{
                        // marginRight: '1rem', // REMOVED
                        fontWeight: 'bold',
                        color: '#6c757d',
                        minWidth: '60px',      // Keep min width for alignment
                        textAlign: 'left',     // CHANGED to left
                        // No flex properties needed, space-between in parent handles position
                      }}
                    >
                      £{item.cost.toFixed(2)}
                    </span>
                    {/* Delete Button - No style changes needed here */}
                    <button
                      style={removeButtonStyle} // Base style is sufficient
                      onClick={() => removeFromCart(item.id)}
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Total Price */}
            <div style={totalContainerStyle}>
              <h4 style={totalStyle}>Total:</h4>
              <h4 style={totalPriceStyle}>£{formattedTotalPrice}</h4>
            </div>

            {/* Footer Buttons */}
            <div style={dynamicButtonsContainerStyle}>
              <button
                style={dynamicClearCartButtonStyle}
                onClick={() => clearCart()}
                disabled={cartItems.length === 0}
              >
                <FaTrash style={{ marginRight: '6px', verticalAlign: 'middle' }} size={14} />
                Clear Cart
              </button>

              <Link to="/booking" style={dynamicBookRepairButtonStyle} onClick={onClose}>
                {'Book a Repair'}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ======================== */
/* === INLINE STYLES === */
/* ======================== */

// *** MODIFIED itemActionsStyle ***
const itemActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between', // CHANGED: Use space-between
  flexShrink: 0,
  minWidth: '120px', // ADDED: Ensure minimum width for separation (adjust as needed)
  // gap: '0.75rem', // REMOVED: space-between handles spacing
};


// (Other styles remain the same as in the previous version)

const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0,0,0,0.6)',
  zIndex: 1050,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '1rem',
};

const modalContentStyle = {
  backgroundColor: '#fff',
  color: '#6c757d',
  borderRadius: '8px',
  maxWidth: '100%',
  // maxHeight: '85vh', // Set dynamically
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column',
};

const modalHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '1rem',
  borderBottom: '1px solid #e9ecef',
  marginBottom: '1.5rem',
  flexShrink: 0,
};

const closeIconButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '1.2rem',
  cursor: 'pointer',
  color: '#6c757d',
  padding: '0.5rem',
  lineHeight: '1',
  borderRadius: '50%',
  transition: 'background-color 0.2s ease',
};


const itemListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  flexGrow: 1,
  overflowY: 'auto',
  minHeight: '50px',
};

const itemRowStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.75rem 0.25rem',
  borderBottom: '1px solid #eee',
  // gap: '1rem', // Set dynamically
  // flexWrap: 'nowrap', // Set dynamically
};

const itemImageStyle = {
  // width: '50px', // Set dynamically
  // height: '50px', // Set dynamically
  objectFit: 'cover',
  borderRadius: '4px',
  flexShrink: 0,
};

const itemDetailsStyle = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '0 0.5rem',
  // flexBasis: 'auto', // Set dynamically
};

// ItemActionsStyle is modified above

const removeButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  color: '#dc3545',
  border: '1px solid #dc3545',
  borderRadius: '4px',
  padding: '0.4rem 0.6rem',
  cursor: 'pointer',
  fontSize: '0.85rem',
  transition: 'background-color 0.2s, color 0.2s',
  lineHeight: 1,
};


const totalContainerStyle = {
  marginTop: '1.5rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e9ecef',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  flexShrink: 0,
};

const totalStyle = {
  margin: 0,
  fontSize: '1.1rem',
  fontWeight: '500',
  color: '#6c757d',
};

const totalPriceStyle = {
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '#6c757d',
};

const buttonsContainerStyle = {
  marginTop: '1.5rem',
  display: 'flex',
  gap: '0.75rem', // Use gap for spacing (margin handled by dynamic style)
  flexShrink: 0,
  paddingTop: '1rem',
  borderTop: '1px solid #e9ecef',
};

// BASE STYLE - Used when not mobile
const clearCartButtonStyle = {
  // display: 'inline-flex', // Set dynamically
  alignItems: 'center',
  background: '#6c757d',
  color: '#fff',
  border: '1px solid #6c757d',
  borderRadius: '4px',
  padding: '0.6rem 1.2rem',
  cursor: 'pointer',
  fontSize: '0.9rem',
  transition: 'background-color 0.2s, border-color 0.2s',
  fontWeight: '500',
  textDecoration: 'none',
  lineHeight: 1.2,
};

// BASE STYLE - Used when not mobile
const bookRepairButtonStyle = {
  // display: 'inline-flex', // Set dynamically
  alignItems: 'center',
  background: '#007BFF',
  color: '#fff',
  border: '1px solid #007BFF',
  borderRadius: '4px',
  padding: '0.6rem 1.2rem',
  cursor: 'pointer',
  fontSize: '0.9rem',
  textDecoration: 'none',
  fontWeight: '500',
  transition: 'background-color 0.2s, border-color 0.2s',
  lineHeight: 1.2,
};


const emptyCartStyle = {
  textAlign: 'center',
  padding: '2rem 1rem',
  color: '#6c757d',
  fontSize: '1.1rem',
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default CartModal;